import axios, { AxiosError } from 'axios';
import {
  StudentRegistrationRequest,
  LoginRequest,
  GoogleLoginRequest,
  LoginResponse,
  RegistrationResponse,
  RefreshTokenResponse,
  CompletePhoneResponse,
} from '../types/auth';
import { getDeviceLabel, getOrCreateDeviceId, setDeviceId } from '../utils/deviceId';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Enhanced user-friendly error messages
function getUserFriendlyErrorMessage(error: AxiosError): string {
  if (!error.response) {
    if (error.code === 'NETWORK_ERROR') {
      return 'Không thể kết nối đến server. Vui lòng kiểm tra kết nối internet và thử lại.';
    }
    if (error.code === 'ECONNABORTED') {
      return 'Yêu cầu hết thời gian. Server đang phản hồi chậm. Vui lòng thử lại.';
    }
    return 'Lỗi mạng. Vui lòng kiểm tra kết nối và thử lại.';
  }
  
  const status = error.response.status;
  const data = error.response.data as any;
  const code = data?.code || data?.errors?.[0];

  if (code === 'ACCOUNT_LOCKED' || data?.errors?.includes?.('ACCOUNT_LOCKED')) {
    return 'Tài khoản đã bị khóa do nghi ngờ chia sẻ. Vui lòng liên hệ quản trị viên để mở khóa.';
  }
  if (code === 'DEVICE_LIMIT' || data?.errors?.includes?.('DEVICE_LIMIT')) {
    return 'Đã đạt giới hạn 4 thiết bị. Vui lòng đăng nhập trên thiết bị đã đăng ký hoặc liên hệ admin.';
  }
  
  switch (status) {
    case 400:
      return data?.message || 'Dữ liệu không hợp lệ. Vui lòng kiểm tra thông tin và thử lại.';
    case 401:
      return data?.message || 'Email hoặc mật khẩu không chính xác.';
    case 403:
      return data?.message || 'Tài khoản không có quyền truy cập.';
    case 404:
      return data?.message || 'Không tìm thấy thông tin yêu cầu.';
    case 409:
      return data?.message || 'Email hoặc số điện thoại đã tồn tại.';
    case 429:
      return 'Quá nhiều yêu cầu. Vui lòng đợi một chút và thử lại.';
    case 500:
      return 'Lỗi server. Chúng tôi đã được thông báo. Vui lòng thử lại sau.';
    default:
      return data?.message || `Lỗi không xác định (${status}). Vui lòng thử lại.`;
  }
}

// Add request interceptor for logging and auth headers
api.interceptors.request.use(
  (config) => {
    console.log(`Auth API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add auth header if token exists
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('Auth API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    console.error('Auth API Response Error:', error);
    
    const userMessage = getUserFriendlyErrorMessage(error);
    throw new Error(userMessage);
  }
);

export class AuthApi {
  /**
   * Register a new student account
   */
  static async register(data: StudentRegistrationRequest): Promise<RegistrationResponse> {
    try {
      console.log('Attempting student registration:', { email: data.email });
      
      const response = await api.post<RegistrationResponse>('/auth/register', data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Đăng ký thất bại');
      }
      
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Login with email and password
   */
  static async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      console.log('Attempting login:', { email: data.email });

      const fingerprintHash = data.fingerprintHash || (await getDeviceFingerprint());
      const payload = {
        ...data,
        deviceId: data.deviceId || getOrCreateDeviceId(),
        deviceLabel: data.deviceLabel || getDeviceLabel(),
        fingerprintHash,
      };
      
      const response = await api.post<LoginResponse>('/auth/login', payload);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Đăng nhập thất bại');
      }
      
      // Store tokens in localStorage
      if (response.data.data.accessToken) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
      }
      if (response.data.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
      }
      // Server may return canonical deviceId when fingerprint matched an old slot
      if (response.data.data.deviceId) {
        setDeviceId(response.data.data.deviceId);
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Login with Google ID token (GIS credential)
   */
  static async loginWithGoogle(data: GoogleLoginRequest): Promise<LoginResponse> {
    try {
      const fingerprintHash = data.fingerprintHash || (await getDeviceFingerprint());
      const payload = {
        idToken: data.idToken,
        deviceId: data.deviceId || getOrCreateDeviceId(),
        deviceLabel: data.deviceLabel || getDeviceLabel(),
        fingerprintHash,
      };

      const response = await api.post<LoginResponse>('/auth/google', payload);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Đăng nhập Google thất bại');
      }

      if (response.data.data.accessToken) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
      }
      if (response.data.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
      }
      if (response.data.data.deviceId) {
        setDeviceId(response.data.data.deviceId);
      }

      return response.data;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  /**
   * Complete required phone after login / Google sign-in
   */
  static async completePhone(phone: string): Promise<CompletePhoneResponse> {
    try {
      const response = await api.put<CompletePhoneResponse>('/auth/complete-phone', { phone });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Cập nhật số điện thoại thất bại');
      }
      return response.data;
    } catch (error) {
      console.error('Complete phone error:', error);
      throw error;
    }
  }

  /**
   * Logout user (revoke refresh token)
   */
  static async logout(refreshToken?: string): Promise<void> {
    try {
      const tokenToUse = refreshToken || localStorage.getItem('refreshToken');
      
      if (tokenToUse) {
        await api.post('/auth/logout', { refreshToken: tokenToUse });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Don't throw error on logout failure - still clear local storage
    } finally {
      // Always clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  /**
   * Refresh access token using refresh token
   */
  static async refreshToken(refreshToken?: string): Promise<RefreshTokenResponse> {
    try {
      const tokenToUse = refreshToken || localStorage.getItem('refreshToken');
      
      if (!tokenToUse) {
        throw new Error('Không có refresh token');
      }
      
      const response = await api.post<RefreshTokenResponse>('/auth/refresh', {
        refreshToken: tokenToUse,
        deviceId: getOrCreateDeviceId(),
        deviceLabel: getDeviceLabel(),
        fingerprintHash: await getDeviceFingerprint(),
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Làm mới token thất bại');
      }
      
      // Update tokens in localStorage
      if (response.data.data.accessToken) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
      }
      if (response.data.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
      }
      if (response.data.data.deviceId) {
        setDeviceId(response.data.data.deviceId);
      }
      
      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Clear invalid tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  }

  /**
   * Get current access token from localStorage
   */
  static getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * Get current refresh token from localStorage
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  static isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    return !!(accessToken && refreshToken);
  }

  /**
   * Validate password strength on frontend
   */
  static validatePasswordStrength(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!password) {
      errors.push('Mật khẩu là bắt buộc');
      return { isValid: false, errors };
    }
    
    if (password.length < 8) {
      errors.push('Mật khẩu phải có ít nhất 8 ký tự');
    }
    
    if (password.length > 128) {
      errors.push('Mật khẩu không được quá 128 ký tự');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Mật khẩu phải có ít nhất một chữ cái thường');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Mật khẩu phải có ít nhất một chữ cái hoa');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Mật khẩu phải có ít nhất một số');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Mật khẩu phải có ít nhất một ký tự đặc biệt');
    }
    
    // Check for common weak passwords
    const commonPasswords = [
      'password', '123456', '123456789', 'qwerty', 'abc123',
      'password123', 'admin', 'letmein', 'welcome', 'monkey'
    ];
    
    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('Mật khẩu quá phổ biến và dễ đoán');
    }
    
    return { isValid: errors.length === 0, errors };
  }

  /**
   * Validate email format
   */
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number (Vietnamese format: 10 digits starting with 0)
   */
  static validatePhone(phone: string): boolean {
    const phoneRegex = /^0\d{9}$/;
    return phoneRegex.test(phone);
  }

  /**
   * Request password reset - generates new password and sends via email
   */
  static async forgotPassword(email: string): Promise<void> {
    try {
      console.log('Requesting password reset for:', email);

      const response = await api.post('/auth/forgot-password', { email });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Gửi email thất bại');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  /**
   * Change password for authenticated user
   */
  static async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
    try {
      console.log('Attempting to change password');

      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Đổi mật khẩu thất bại');
      }
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }
}

/**
 * Get the current auth token from localStorage
 */
export const getAuthToken = (): string | null => {
  try {
    // First try to get from Zustand persist storage
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      const parsed = JSON.parse(authStorage);
      const token = parsed.state?.accessToken;
      if (token) return token;
    }
    
    // Fallback to direct localStorage access
    const directToken = localStorage.getItem('accessToken');
    if (directToken) return directToken;
    
    return null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

export default AuthApi;